import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { SessionStorageKeys } from 'chaika-enums';

import '@components/mixins/commonStyles.scss';
import style from '@core/Page/SelectOrganizationPopup/SelectOrganizationPopup.module.scss';

import { Popup } from '@components/Popup';
import { SaveButton } from '@components/ActionButtons/SaveButton';
import { CancelButton } from '@components/ActionButtons/CancelButton';
import { authUserService, defaultOrganizationOption } from '@store/AuthUser/services/authUserService';
import { store } from '@store';
import { SelectOrganizationItem } from '@core/Page/SelectOrganizationPopup/SelectOrganizationItem/SelectOrganizationItem';
import { translate } from '@chaika/i18n/helper';

export interface SelectOrganizationPopupParams {
    onSaveClick?(): void;
}

export const SelectOrganizationPopup = observer(({ onSaveClick }: SelectOrganizationPopupParams) => {
    const { app, authUser } = store;
    const [organizationId, setOrganizationId] = useState<string | undefined>(authUser.organizationId);

    useEffect(() => {
        sessionStorage.setItem(SessionStorageKeys.FromAuthPage, 'false');
    }, []);

    const organizationOptions = authUserService.makeOrganizationOptions();

    const handleOrganizationSelect = useCallback(
        (organizationIds: string[]) => {
            const selectedId = organizationId
                ? organizationIds.filter(id => id !== organizationId)[0]
                : organizationIds[0];

            if (!selectedId) return;

            setOrganizationId(selectedId);
        },
        [organizationId],
    );

    const handleCancelClick = useCallback(() => {
        app.resetSelectOrganizationPopupParams();
    }, [app]);

    const handleSaveClick = useCallback(async () => {
        if (organizationId === defaultOrganizationOption.value) {
            await authUserService.updateSelectedOrganization();
        } else if (organizationId) {
            await authUserService.updateSelectedOrganization(organizationId);
        }

        app.resetSelectOrganizationPopupParams();

        if (onSaveClick) {
            await onSaveClick();
        }
    }, [app, organizationId, onSaveClick]);

    const hasOptions = organizationOptions.length > 0;

    return (
        <Popup onOutOfContentClick={handleCancelClick}>
            <div className={style.root}>
                <div className={classnames([style.title, 'font_h2'])}>{translate('selectOrganizationToReceive')}</div>
                <div className={style.organizations}>
                    {hasOptions ? (
                        <>
                            {organizationOptions.map(organization => (
                                <SelectOrganizationItem
                                    key={organization.value}
                                    onOrganizationSelect={handleOrganizationSelect}
                                    organization={organization}
                                    selectedValues={organizationId ? [organizationId] : []}
                                />
                            ))}
                        </>
                    ) : (
                        <span className={style.text}>{translate('noClinicsAvailable')}</span>
                    )}
                </div>
                <div className={style.actionButtons}>
                    <SaveButton
                        disabled={!organizationId}
                        onSaveClick={handleSaveClick}
                    />
                    <CancelButton onCancelClick={handleCancelClick} />
                </div>
            </div>
        </Popup>
    );
});
